import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import { NgStyle, AsyncPipe, SlicePipe } from '@angular/common';
// Ag-Grid
import { AgGridAngular } from 'ag-grid-angular';
import {
  GridOptions,
  GridApi,
  ColDef,
  CellContextMenuEvent,
  RowSelectionOptions,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
// Third Party
import {
  DateFormats,
  ModalID,
  Statements,
  Questions,
  ModalType,
  EmailType,
} from 'src/app/shared/models/constants';
import { format } from 'date-fns';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';
import { AccountingService } from 'src/app/accounting/services/accounting.service';
import { ContextMenuService } from 'src/app/shared/services/context-menu.service';
import { FFL, Email } from '../../models/general';
import { FFLSearchColumnDef } from './columnDefs';
// functions
import { getLastFiveFFLs, getTopFiveFFLs } from '../../functions';

@Component({
  selector: 'app-ffl-search',
  templateUrl: './ffl-search.component.html',
  styleUrl: './ffl-search.component.scss',
  standalone: true,
  imports: [NgStyle, AgGridAngular, AsyncPipe, SlicePipe],
})
export class FflSearchComponent implements OnInit, AfterViewInit {
  @ViewChild('grid', { read: ElementRef }) gridElement!: ElementRef;
  @ViewChild('grid') grid!: AgGridAngular; // Keep for grid API access
  ffl_selected: FFL = new FFL();
  today: string = format(new Date(), DateFormats.M_D_Y);

  // ag-grid properties Grid
  gridApi!: GridApi;

  columnDefs: ColDef[] = FFLSearchColumnDef;
  defaultColDef;
  detailCellRendererParams: any;
  overlayLoadingTemplate;
  overlayNoRowsTemplate;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    rowSelection: {
      mode: 'singleRow',
      enableSelectionWithoutKeys: true,
      checkboxes: false,
    } as RowSelectionOptions,
    rowHeight: 30,
    preventDefaultOnContextMenu: true,
  };
  components: any;
  theme = agGridDarkTheme;

  // Context Menu state
  menuState$ = this.contextMenuService.menuState$;
  contextMenuRow: FFL | null = null;

  constructor(
    protected element: ElementRef,
    public sharedService: SharedService,
    public accountingService: AccountingService,
    private contextMenuService: ContextMenuService,
    private cdr: ChangeDetectorRef,
  ) {
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
    };
  }
  ngOnInit(): void {
    getLastFiveFFLs(this.sharedService);
    getTopFiveFFLs(this.sharedService);
  }
  ngAfterViewInit() {
    this.contextMenuService.initialize(this.cdr, this.gridElement);
  }
  onCardClick(event: MouseEvent, ffl: FFL): void {
    if (window.matchMedia('(max-width: 1440px)').matches) {
      event.preventDefault();
      event.stopPropagation();
      this.fflDoubleClicked(ffl);
    }
  }
  onCardDoubleClick(event: MouseEvent, ffl: FFL): void {
    event.preventDefault();
    event.stopPropagation();
    this.fflDoubleClicked(ffl);
  }
  fflDoubleClicked(ffl: FFL): void {
    const expirationDate = new Date(ffl.expiration_date);
    const todayDate = new Date(this.today);
    if (expirationDate < todayDate) {
      this.sharedService.raiseInfoToast(Statements.ffl_expired);
    } else {
      this.closeModalClick();
      this.sharedService.ffl_selected$.next(ffl);
    }
  }
  onRowDoubleClicked(event: any): void {
    const ffl: FFL = event.data;
    this.fflDoubleClicked(ffl);
  }
  getStyleForFFL(ffl: FFL): any {
    if (!ffl || !ffl.expiration_date) return { color: 'red' }; // Default to expired style if no date
    const expirationDate = new Date(ffl.expiration_date);
    const todayDate = new Date(this.today);
    return expirationDate >= todayDate ? { color: 'black' } : { color: 'red' };
  }
  isExpired(ffl: FFL): boolean {
    if (!ffl || !ffl.expiration_date) return true; // Treat as expired if no date exists
    const expirationDate = new Date(ffl.expiration_date);
    const todayDate = new Date(this.today);
    return expirationDate < todayDate;
  }
  onRightClick(event: MouseEvent, ffl: FFL) {
    event.preventDefault();
    if (this.isExpired(ffl)) {
      this.contextMenuRow = ffl;
      this.contextMenuService.onRightClick(event);
    }
  }
  closeModalClick(): void {
    this.sharedService.closeModal(ModalID.ffl_search);
  }

  // Context Menu Methods
  onCellContextMenu(event: CellContextMenuEvent) {
    this.contextMenuRow = event.data;
    this.contextMenuService.onCellContextMenu(event);
  }
  onMenuMouseEnter() {
    this.contextMenuService.onMenuMouseEnter();
  }
  onMenuMouseLeave() {
    this.contextMenuService.onMenuMouseLeave();
  }
  copyCellValue() {
    this.contextMenuService.copyCellValue();
    this.contextMenuRow = null;
  }
  editFFL() {
    if (this.contextMenuRow) {
      this.sharedService.openAddEditFFLModal(
        ModalType.edit,
        this.contextMenuRow,
      );
      // Attempt to close menu by simulation or letting blur handle it
      // Triggering a click elsewhere usually closes it via service's document listener
      document.body.click();
    }
  }
  requestNewFFL() {
    if (this.contextMenuRow) {
      const email = new Email();
      email.to = this.contextMenuRow.email;
      email.subject =
        this.sharedService.company_info.email_templates['ffl_request']
          ?.subject || '';
      email.body =
        this.sharedService.company_info.email_templates['ffl_request']?.body ||
        '';
      const email_type = EmailType.ffl;
      this.sharedService.openEmailModal(email_type, email);
      document.body.click();
    }
  }
  async viewAttachment(event: Event, fflId: string) {
    event.stopPropagation();
    event.preventDefault();
    try {
      const url = await this.sharedService.getCertifiedFflPdfUrl(fflId);
      window.open(url, '_blank');
    } catch (e) {
      this.sharedService.raiseErrorToast('Could not load PDF link.');
    }
  }
}
